import { Component, OnInit, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CancelServiceordreDialogData, CancelServiceordreDialogResponse } from '../../aftale/models';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { catchError, defer, finalize, Observable, of, Subject, takeUntil } from 'rxjs';
import { LoaderService } from 'src/app/services/loader.service';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { ProduktEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/produkt-enum';
import { PartnerBaseDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/partner-base-dto';
import { PartnerService } from 'src/app/api/services';
import { OpgaveEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/opgave-enum';

@Component({
    selector: 'app-cancel-ordre-dialog',
    templateUrl: './cancel-ordre-dialog.component.html',
    styleUrls: ['./cancel-ordre-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CancelOrdreDialogComponent implements OnInit {
  form: UntypedFormGroup;
  produkt: ProduktEnum;
  ordreType: OpgaveEnum;
  activePartners$: Observable<PartnerBaseDto[]>;
  unsubscribe$ = new Subject<void>();

  get noteControl() {
    return this.form.get('note');
  }
  get partnerNo() {
    return this.form.get('partnerNo');
  }
  get resendToNewPartner() {
    return this.form.get('resendToNewPartner');
  }

  constructor(
    private builder: UntypedFormBuilder,
    private partnerService: PartnerService,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    public dialogRef: MatDialogRef<CancelOrdreDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CancelServiceordreDialogData
  ) {
    this.produkt = data.produkt;
    this.ordreType = data.ordreType;
    this.form = this.builder.group({
      note: ['', [Validators.required, Validators.minLength(3)]],
      partnerNo: [null],
      resendToNewPartner: [false],
    });
  }

  message: string;

  ngOnInit(): void {
    this.setDialogMessage(this.ordreType);
    this.activePartners$ = defer(() => {
      this.loader.show();
      return this.partnerService
        .getActivePartnere({
          caller: EGIS_SERVICE_CALLER,
        })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
          catchError((err) => {
            this.errorHandler.HandleError(err);
            return of(null);
          })
        );
    });
    this.resendToNewPartner.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((val) => {
      if (val) {
        this.partnerNo.setValidators([Validators.required]);
      } else {
        this.partnerNo.clearValidators();
      }
    });
  }

  setDialogMessage(type: OpgaveEnum) {
    switch (type) {
      case OpgaveEnum.Eftersyn:
        this.message =
          'Du er ved at annullere et eftersyn. Ordren kan være planlagt og du skal derfor kontakte S-partneren. Vil du fortsætte annullering? Vær obs om opkrævningsdato skal rykkes og om kunden skal have frataget de frie tilkald.';
        break;
      case OpgaveEnum.Tilkald:
        this.message =
          'Du er ved at annullere et tilkald. Ordren kan være planlagt og du skal derfor kontakte S-partneren. Vil du fortsætte annullering?';
        break;
      case OpgaveEnum.Opstart:
        this.message =
          'Du er ved at annullere en opstart. Ordren kan være planlagt og du skal derfor kontakte S-partneren. Vil du fortsætte annullering?';
        break;
      default:
        this.message =
          'Husk at bekræfte med S-partner inden opgaver annulleres. Opgaven kan være planlagt og skal derfor bekræftes af partner. Vil du fortsætte annullering?';
        break;
    }
  }

  cancel() {
    this.dialogRef.close(false);
  }

  create() {
    const result: CancelServiceordreDialogResponse = {
      confirm: true,
      note: this.noteControl.value,
      resendToNewPartner: this.resendToNewPartner.value,
      newPartnerNo: this.partnerNo.value,
    };
    this.dialogRef.close(result);
  }
}
